import React, { useState, useRef } from "react";
import styled from "styled-components";
import { Button, Switch, Tooltip, Modal, Form, Input, notification } from "antd";
import {
    PlusOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
    getResources,
    createResource,
    updateResourceCategory,
    deleteResourceCategory,
    deleteResource,
} from "../../../service";
import { colors, spacing, sizes, transitions } from "../../../styles/theme";
import { EditResourceModal } from "../edit-resource-modal/EditResourceModal";

const CategoryContainer = styled.div`
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius};
  border: 1px solid ${colors.gray200};
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: ${spacing.md};
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  padding: 0 ${spacing.xxxl};
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};
    border-radius: ${spacing.md} ${spacing.md} 0 0;
  }
`;

const CategoryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
`;

const VisibilityBadge = styled.span<{ visible: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: ${props => props.visible ? colors.successBackground : colors.gray200};
  border-radius: 4px;
  font-size: 11px;
  color: ${props => props.visible ? colors.success : colors.textSecondary};
  font-weight: 500;
  white-space: nowrap;
`;

const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
`;

const ArrowButton = styled(Button)`
  
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    &.edit-button {
      display: none;
    }
  }
`;

const ResourcesList = styled.div<{ hasScroll?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 ${spacing.lg} ${spacing.lg} ${spacing.lg};
  ${props => props.hasScroll && `
    max-height: calc(4.5 * 80px + ${spacing.lg});
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.gray50};
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.gray300};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.gray400};
    }
  `}

  @media (max-width: 768px) {
    padding: 0 ${spacing.md} ${spacing.md} ${spacing.md};
  }
`;

const ResourceRow = styled.div`
  background: ${colors.white};
  border-bottom: 1px solid ${colors.borderLight};
  padding: 0 ${spacing.lg};
  height: 64px;
  min-height: 64px;
  max-height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: ${transitions.normal};

  &:first-child {
    border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 ${sizes.borderRadius} ${sizes.borderRadius};
  }

  &:first-child:last-child {
    border-radius: ${sizes.borderRadius};
  }

  &:hover {
    background: ${colors.gray200};
  }

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};

    &:first-child {
      border-radius: ${spacing.md} ${spacing.md} 0 0;
    }

    &:last-child {
      border-radius: 0 0 ${spacing.md} ${spacing.md};
    }

    &:first-child:last-child {
      border-radius: ${spacing.md};
    }
  }
`;

const ResourceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex: 1;
`;

const ResourceName = styled.span`
  font-size: 14px;
  color: ${colors.textPrimary};
  font-weight: 500;
`;

const ResourceActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const AddButtonText = styled.span`
  .mobile-text {
    display: none;
  }

  @media (max-width: 768px) {
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: inline;
    }
  }
`;

interface ResourceCategorySectionProps {
    category: any;
}

export const ResourceCategorySection: React.FC<ResourceCategorySectionProps> = ({
    category,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [resources, setResources] = useState<any[]>([]);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isAddResourceModalOpen, setIsAddResourceModalOpen] = useState(false);
    const [isEditResourceModalOpen, setIsEditResourceModalOpen] = useState(false);
    const [editingResourceId, setEditingResourceId] = useState<number | null>(null);
    const [categoryForm] = Form.useForm();
    const [addResourceForm] = Form.useForm();
    const resourcesListRef = useRef<HTMLDivElement>(null);

    useQuery<any, Error>(
        [`/resources-category-${category.id}`],
        () => getResources({ id: category.id }),
        {
            onSuccess(data) {
                setResources(data);
            },
        }
    );

    const { mutateAsync: createResourceMutation, isLoading: isCreatingResource } = useMutation(
        createResource,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([`/resources-category-${category.id}`]);
                notification.success({
                    message: t("resourceCategorySection.resourceCreated"),
                    placement: "bottomRight",
                });
                addResourceForm.resetFields();
                setIsAddResourceModalOpen(false);
            },
            onError: () => {
                notification.error({
                    message: t("resourceCategorySection.resourceCreateError"),
                    placement: "bottomRight",
                });
            },
        }
    );



    const { mutateAsync: deleteResourceMutation } = useMutation(
        deleteResource,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([`/resources-category-${category.id}`]);
                notification.success({
                    message: t("resourceCategorySection.resourceDeleted"),
                    placement: "bottomRight",
                });
            },
            onError: () => {
                notification.error({
                    message: t("resourceCategorySection.resourceDeleteError"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const { mutateAsync: updateCategoryMutation } = useMutation(
        updateResourceCategory,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["/resource-categories"]);
                notification.success({
                    message: t("resourceCategorySection.categoryUpdated"),
                    placement: "bottomRight",
                });
                setIsCategoryModalOpen(false);
            },
            onError: () => {
                notification.error({
                    message: t("resourceCategorySection.categoryUpdateError"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const { mutateAsync: deleteCategoryMutation } = useMutation(
        deleteResourceCategory,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["/resource-categories"]);
                notification.success({
                    message: t("resourceCategorySection.categoryDeleted"),
                    placement: "bottomRight",
                });
                setIsCategoryModalOpen(false);
            },
            onError: () => {
                notification.error({
                    message: t("resourceCategorySection.categoryDeleteError"),
                    description: t("resourceCategorySection.categoryDeleteErrorDescription"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const handleAddResource = () => {
        setIsAddResourceModalOpen(true);
    };

    const handleAddResourceSubmit = async () => {
        try {
            const values = await addResourceForm.validateFields();
            await createResourceMutation({
                ...values,
                resourceCategoryId: category.id,
            });
        } catch {
            // validation failed
        }
    };

    const handleEditResource = (resource: any) => {
        setEditingResourceId(resource.id);
        setIsEditResourceModalOpen(true);
    };



    const handleDeleteResource = (resourceId: number) => {
        Modal.confirm({
            title: t("resourceCategorySection.deleteResourceConfirmTitle"),
            okText: t("resourceCategorySection.delete"),
            okType: "danger",
            cancelText: t("resourceCategorySection.cancel"),
            onOk: async () => {
                await deleteResourceMutation(resourceId);
            },
        });
    };

    const handleOpenCategoryModal = () => {
        categoryForm.setFieldsValue({
            name: category.name,
            visibleForClient: category.visibleForClient,
        });
        setIsCategoryModalOpen(true);
    };

    const handleUpdateCategory = async () => {
        const values = await categoryForm.validateFields();
        await updateCategoryMutation({
            id: category.id,
            body: values,
        });
    };

    const handleDeleteCategory = () => {
        Modal.confirm({
            title: t("resourceCategorySection.deleteCategoryConfirmTitle"),
            content: t("resourceCategorySection.deleteCategoryConfirmContent"),
            okText: t("resourceCategorySection.delete"),
            okType: "danger",
            cancelText: t("resourceCategorySection.cancel"),
            onOk: async () => {
                await deleteCategoryMutation(category.id);
            },
        });
    };

    const hasScroll = resources.length > 5;

    return (
        <>
            <CategoryContainer>
                <CategoryHeader>
                    <CategoryTitleRow>
                        <CategoryTitle onClick={handleOpenCategoryModal}>
                            {category.name}
                        </CategoryTitle>
                        {category.visibleForClient !== undefined && (
                            <VisibilityBadge visible={category.visibleForClient}>
                                {category.visibleForClient ? t("resourceCategorySection.visible") : t("resourceCategorySection.hidden")}
                            </VisibilityBadge>
                        )}
                        <Tooltip title={t("resourceCategorySection.editCategoryTooltip")}>
                            <ArrowButton
                                type="text"
                                icon={<EditOutlined />}
                                onClick={handleOpenCategoryModal}
                                className="edit-button"
                            />
                        </Tooltip>
                    </CategoryTitleRow>
                    <CategoryActions>
                        <Button
                            type="default"
                            size="middle"
                            icon={<PlusOutlined />}
                            onClick={handleAddResource}
                        >
                            <AddButtonText>
                                <span className="desktop-text">{t("resourceCategorySection.addResource")}</span>
                                <span className="mobile-text">{t("resourceCategorySection.resource")}</span>
                            </AddButtonText>
                        </Button>
                    </CategoryActions>
                </CategoryHeader>

                <ResourcesList ref={resourcesListRef} hasScroll={hasScroll}>
                    {resources.map((resource) => (
                        <ResourceRow key={resource.id}>
                            <ResourceInfo>
                                <ResourceName>{resource.name}</ResourceName>
                            </ResourceInfo>
                            <ResourceActions>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => handleEditResource(resource)}
                                >
                                    {t("resourceCategorySection.edit")}
                                </Button>
                                <Button
                                    type="link"
                                    size="small"
                                    danger
                                    onClick={() => handleDeleteResource(resource.id)}
                                >
                                    {t("resourceCategorySection.delete")}
                                </Button>
                            </ResourceActions>
                        </ResourceRow>
                    ))}

                    {resources.length === 0 && (
                        <ResourceRow onClick={handleAddResource} style={{ cursor: "pointer" }}>
                            <ResourceInfo>
                                <ResourceName style={{ color: colors.textSecondary }}>
                                    {t("resourceCategorySection.noResourcesInCategory")}
                                </ResourceName>
                            </ResourceInfo>
                        </ResourceRow>
                    )}
                </ResourcesList>
            </CategoryContainer>

            {/* Edit category modal */}
            <Modal
                title={t("resourceCategorySection.editCategoryModalTitle")}
                open={isCategoryModalOpen}
                onOk={handleUpdateCategory}
                onCancel={() => setIsCategoryModalOpen(false)}
                okText={t("resourceCategorySection.save")}
                cancelText={t("resourceCategorySection.cancel")}
                footer={[
                    <Button key="delete" danger onClick={handleDeleteCategory}>
                        {t("resourceCategorySection.deleteCategory")}
                    </Button>,
                    <Button key="cancel" onClick={() => setIsCategoryModalOpen(false)}>
                        {t("resourceCategorySection.cancel")}
                    </Button>,
                    <Button key="save" type="primary" onClick={handleUpdateCategory}>
                        {t("resourceCategorySection.save")}
                    </Button>,
                ]}
            >
                <Form form={categoryForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t("resourceCategorySection.categoryNameLabel")}
                        rules={[{ required: true, message: t("resourceCategorySection.categoryNameRequired") }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="visibleForClient"
                        label={t("resourceCategorySection.visibleForClientLabel")}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add resource modal */}
            <Modal
                title={t("resourceCategorySection.addResourceModalTitle")}
                open={isAddResourceModalOpen}
                onOk={handleAddResourceSubmit}
                onCancel={() => {
                    addResourceForm.resetFields();
                    setIsAddResourceModalOpen(false);
                }}
                okText={t("resourceCategorySection.create")}
                cancelText={t("resourceCategorySection.cancel")}
                confirmLoading={isCreatingResource}
            >
                <Form form={addResourceForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t("resourceCategorySection.resourceNameLabel")}
                        rules={[{ required: true, message: t("resourceCategorySection.resourceNameRequired") }]}
                    >
                        <Input placeholder={t("resourceCategorySection.resourceNamePlaceholder")} autoFocus />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit resource modal */}
            <EditResourceModal
                open={isEditResourceModalOpen}
                resourceId={editingResourceId}
                categoryId={category.id}
                onCancel={() => {
                    setIsEditResourceModalOpen(false);
                    setEditingResourceId(null);
                }}
                onSuccess={() => {
                    setIsEditResourceModalOpen(false);
                    setEditingResourceId(null);
                    queryClient.invalidateQueries([`/resources-category-${category.id}`]);
                }}
            />
        </>
    );
};
